import { MARKETING_ARTICLES } from '@/data/marketing-articles';
import { useLocalizedRoute } from '@/hooks/use-localized-route';
import { LOCALE_DISPLAY_NAMES } from '@/utils/locale-url-helper';
import { useTranslation } from 'react-i18next';

export default function Footer() {
    const { t } = useTranslation();
    const route = useLocalizedRoute();

    const navigation = {
        knowledge: MARKETING_ARTICLES.map((article) => ({
            name: t(`home.footer.navigation.${article.footerKey}`),
            href: route(article.route),
        })),
        main: [
            {
                name: t('home.footer.navigation.help'),
                href: route('help'),
            },
            {
                name: t('home.footer.navigation.verbaende'),
                href: route('verbaende'),
            },
            {
                name: t('home.footer.navigation.presse'),
                href: route('presse'),
            },
            {
                name: t('home.footer.navigation.impressum'),
                href: route('impressum'),
            },
            {
                name: t('home.footer.navigation.datenschutz'),
                href: route('datenschutz'),
            },
            {
                name: t('home.footer.navigation.agb'),
                href: route('agb'),
            },
        ],
        social: [
            {
                name: 'Facebook',
                href: 'https://www.facebook.com/people/heykaution/61579747530727/',
                target: '_blank',
                rel: 'noopener noreferrer',
                icon: (props: React.SVGProps<SVGSVGElement>) => (
                    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                        <path
                            fillRule="evenodd"
                            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                            clipRule="evenodd"
                        />
                    </svg>
                ),
            },
            {
                name: 'Instagram',
                href: 'https://www.instagram.com/heykaution/',
                target: '_blank',
                rel: 'noopener noreferrer',
                icon: (props: React.SVGProps<SVGSVGElement>) => (
                    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                        <path
                            fillRule="evenodd"
                            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                            clipRule="evenodd"
                        />
                    </svg>
                ),
            },
            {
                name: 'LinkedIn',
                href: 'https://linkedin.com/showcase/heykaution',
                target: '_blank',
                rel: 'noopener noreferrer',
                icon: (props: React.SVGProps<SVGSVGElement>) => (
                    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                ),
            },
        ],
        language: [
            {
                name: LOCALE_DISPLAY_NAMES.de,
                href: route('home', { locale: 'de' }),
            },
            {
                name: LOCALE_DISPLAY_NAMES.en,
                href: route('home', { locale: 'en' }),
            },
        ],
    };

    return (
        <footer className="bg-gray-100 dark:bg-gray-950">
            <div className="mx-auto max-w-7xl overflow-hidden px-6 py-10 sm:py-12 lg:px-8">
                {/* Two-column navigation for knowledge and legal */}
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-12 lg:gap-16">
                    {/* Knowledge Section */}
                    <nav aria-label="Knowledge">
                        <h3 className="mb-4 text-sm font-semibold tracking-wider text-headline uppercase">
                            {t('home.footer.sections.knowledge')}
                        </h3>
                        <ul className="space-y-3">
                            {navigation.knowledge.map((item) => (
                                <li key={item.name}>
                                    <a href={item.href} className="text-md/6 text-paragraph hover:text-hs-blue dark:hover:text-hs-green-light">
                                        {item.name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    {/* Legal Section */}
                    <nav aria-label="Legal">
                        <h3 className="mb-4 text-sm font-semibold tracking-wider text-headline uppercase">
                            {t('home.footer.sections.legal')}
                        </h3>
                        <ul className="space-y-3">
                            {navigation.main.map((item) => (
                                <li key={item.name}>
                                    <a href={item.href} className="text-md/6 text-paragraph hover:text-hs-blue dark:hover:text-hs-green-light">
                                        {item.name}
                                    </a>
                                </li>
                            ))}
                            <li>
                                <a
                                    href="https://plusforta.de/widerruf?source=heykaution"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-md/6 text-paragraph hover:text-hs-blue dark:hover:text-hs-green-light"
                                >
                                    {t('home.footer.navigation.widerruf')}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (
                                            typeof window !== 'undefined' &&
                                            '__ucCmp' in window &&
                                            typeof (window as Window & { __ucCmp?: { showSecondLayer: () => void } }).__ucCmp?.showSecondLayer ===
                                                'function'
                                        ) {
                                            (window as Window & { __ucCmp: { showSecondLayer: () => void } }).__ucCmp.showSecondLayer();
                                        }
                                    }}
                                    className="text-md/6 text-paragraph hover:text-hs-blue dark:hover:text-hs-green-light"
                                >
                                    {t('home.footer.navigation.privacySettings')}
                                </a>
                            </li>
                        </ul>
                    </nav>

                    {/* Language Section */}
                    <nav aria-label="Language">
                        <h3 className="mb-4 text-sm font-semibold tracking-wider text-headline uppercase">
                            {t('home.footer.sections.language')}
                        </h3>
                        <ul className="space-y-3">
                            {navigation.language.map((item) => (
                                <li key={item.name}>
                                    <a href={item.href} className="text-md/6 text-paragraph hover:text-hs-blue dark:hover:text-hs-green-light">
                                        {item.name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>
                <div className="mt-16 flex justify-center gap-x-10">
                    {navigation.social.map((item) => (
                        <a
                            key={item.name}
                            href={item.href}
                            target={item.target}
                            rel={item.rel}
                            className={`text-paragraph ${
                                item.name === 'Instagram'
                                    ? 'hover:text-pink-500 dark:hover:text-pink-400'
                                    : 'hover:text-hs-blue dark:hover:text-hs-blue-light'
                            }`}
                        >
                            <span className="sr-only">{item.name}</span>
                            <item.icon aria-hidden="true" className={item.name === 'LinkedIn' ? 'size-5' : 'size-6'} />
                        </a>
                    ))}
                </div>
                <p className="text-md/6 mt-10 text-center text-paragraph">
                    &copy; {new Date().getFullYear()}{' '}
                    <a href="https://plusforta.de" className="hover:text-gray-900 dark:hover:text-white">
                        {t('home.footer.companyName')}
                    </a>
                    . {t('home.footer.copyright')}
                </p>
            </div>
        </footer>
    );
}
